import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import { useDevice } from '../Device';
import styles from './Carousel.module.scss';
import { CarouselHeader } from './CarouselHeader';
import { CarouselViewport } from './CarouselViewport';
export const GetCarouselTID = (parentTestId)=>getTestIds(parentTestId);
const FALLBACK_ITEM_WIDTH_PX = 240;
const computeCanScrollLeft = (node)=>node.scrollLeft > 0;
const computeCanScrollRight = (node)=>node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
export const Carousel = (props)=>{
    const { items, equalItemWidth = true, itemMinWidth = FALLBACK_ITEM_WIDTH_PX, gap = 16, fade = true, arrows = true, collapsible = false, collapsed, defaultCollapsed = false, onCollapsedChange, header, dragMode = 'withGhost', dragHandleVisibility = 'always', dragActivation = 'full', dragHandlePosition = 'inside', autoScrollToEndOnItemAdd = false, scrollLeftAriaLabel = 'Scroll left', scrollRightAriaLabel = 'Scroll right', collapseAriaLabel = 'Collapse', expandAriaLabel = 'Expand', className, testId, bi } = props;
    const reorderProps = props.reorderable ? {
        reorderable: true,
        onReorder: props.onReorder,
        reorderBiName: props.reorderBiName
    } : {
        reorderable: false
    };
    const { CarouselTestIds } = useMemo(()=>GetCarouselTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const logBiAction = useBiAction();
    const [scrollerNode, setScrollerNode] = useState(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const isControlledCollapse = collapsed != null;
    const effectiveCollapsed = collapsible && (collapsed !== null && collapsed !== void 0 ? collapsed : internalCollapsed);
    const toggleCollapsed = useCallback(()=>{
        const next = !effectiveCollapsed;
        if (!isControlledCollapse) setInternalCollapsed(next);
        onCollapsedChange === null || onCollapsedChange === void 0 ? void 0 : onCollapsedChange(next);
        if ((bi === null || bi === void 0 ? void 0 : bi.name) != null) {
            logBiAction({
                entityType: 'carousel',
                entityName: bi.name,
                actionType: next ? 'close' : 'open',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    collapsed: String(next)
                })
            });
        }
    }, [
        bi,
        effectiveCollapsed,
        isControlledCollapse,
        logBiAction,
        onCollapsedChange,
        testId
    ]);
    useEffect(()=>{
        if (scrollerNode == null) return undefined;
        const refresh = ()=>{
            setCanScrollLeft(computeCanScrollLeft(scrollerNode));
            setCanScrollRight(computeCanScrollRight(scrollerNode));
        };
        refresh();
        scrollerNode.addEventListener('scroll', refresh, {
            passive: true
        });
        const resizeObserver = new ResizeObserver(refresh);
        resizeObserver.observe(scrollerNode);
        return ()=>{
            scrollerNode.removeEventListener('scroll', refresh);
            resizeObserver.disconnect();
        };
    }, [
        scrollerNode,
        items.length
    ]);
    const previousItemCountRef = useRef(items.length);
    useEffect(()=>{
        const previousCount = previousItemCountRef.current;
        previousItemCountRef.current = items.length;
        if (!autoScrollToEndOnItemAdd) return;
        if (scrollerNode == null) return;
        const isNewItemAdded = previousCount > 0 && items.length > previousCount;
        if (!isNewItemAdded) return;
        scrollerNode.scrollTo({
            left: scrollerNode.scrollWidth,
            behavior: 'smooth'
        });
    }, [
        items.length,
        autoScrollToEndOnItemAdd,
        scrollerNode
    ]);
    const scrollByOneItem = useCallback((direction)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) != null) {
            logBiAction({
                entityType: 'icon_button',
                entityName: "".concat(bi.name, "_scroll_").concat(direction),
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        if (scrollerNode == null) return;
        const step = itemMinWidth + gap;
        scrollerNode.scrollBy({
            left: direction === 'right' ? step : -step,
            behavior: 'smooth'
        });
    }, [
        bi,
        gap,
        itemMinWidth,
        logBiAction,
        scrollerNode,
        testId
    ]);
    const showArrows = arrows && !isMobileDevice;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.root, className),
        "data-testid": CarouselTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(CarouselHeader, {
                collapsible: collapsible,
                isCollapsed: effectiveCollapsed,
                header: header,
                headerTestId: CarouselTestIds.Header(),
                toggleTestId: CarouselTestIds.CollapseToggle(),
                expandAriaLabel: expandAriaLabel,
                collapseAriaLabel: collapseAriaLabel,
                onToggle: toggleCollapsed
            }),
            !effectiveCollapsed && /*#__PURE__*/ _jsx(CarouselViewport, _object_spread_props(_object_spread({
                ref: setScrollerNode,
                items: items,
                equalItemWidth: equalItemWidth,
                itemMinWidth: itemMinWidth,
                gap: gap,
                fade: fade,
                showArrows: showArrows,
                canScrollLeft: canScrollLeft,
                canScrollRight: canScrollRight
            }, reorderProps), {
                dragMode: dragMode,
                handleVisibility: dragHandleVisibility,
                dragActivation: dragActivation,
                handlePosition: dragHandlePosition,
                viewportTestId: CarouselTestIds.Viewport(),
                scrollerTestId: CarouselTestIds.Scroller(),
                itemTestId: CarouselTestIds.Item,
                scrollLeftTestId: CarouselTestIds.ScrollLeftButton(),
                scrollRightTestId: CarouselTestIds.ScrollRightButton(),
                scrollLeftAriaLabel: scrollLeftAriaLabel,
                scrollRightAriaLabel: scrollRightAriaLabel,
                onScroll: scrollByOneItem
            }))
        ]
    });
};
